/*
 * Image
 *
 * Supports:
 * - Retina
 * - Loading thumbnail
 * - Lazy loading of full resolution src
 * - Webp src for suitable browsers
 */

import React from 'react';
import PropTypes from 'prop-types';
import QueryString from 'query-string';

import Util from '$utilities';

let waiting = {};
let loop = false;
let webp;

const MAX_WIDTH = 1440;

/**
 * Check for WEBP browser support
 */
export const supportsWebp = async () => {
    if (typeof webp !== 'boolean') {
        if (!self.createImageBitmap) {
            return false;
        }
        const webpData = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
        const blob = await fetch(webpData).then(r => r.blob());
        await createImageBitmap(blob).then(() => {
            webp = true;
        }, () => {
            webp = false;
        });
    }
    return webp;
};

/**
 * Global animation frame to check for images in view
 */
const scrollFrame = () => {
    Object.keys(waiting).forEach((uid) => {
        if (waiting[uid]) {
            if (waiting[uid].containerRef.current.offsetParent) {
                const r = waiting[uid].containerRef.current.getBoundingClientRect();
                if (r.top - r.height - window.innerHeight <= 0) {
                    waiting[uid].load();
                    delete waiting[uid];
                }
            }
        }
    });

    window.requestAnimationFrame(scrollFrame);
};

/**
 * Load all images
 */
const loadAll = () => {
    Object.keys(waiting).forEach((uid) => {
        if (waiting[uid]) {
            waiting[uid].load();
            delete waiting[uid];
        }
    });
};


/**
 * Class representing an image
 * @props (see propTypes)
 */
export default class Image extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            loaded: false,
            loading: false,
            src: null,
            x2: null
        };

        this.uid = Util.getId();
        this.imageRef = React.createRef();
        this.containerRef = React.createRef();
    }

    // Setup loading process
    componentDidMount() {
        this._isMounted = true;
        waiting[this.uid] = this;
        // Trigger all images to load when printing document
        if (!window.onbeforeprint) {
            window.onbeforeprint = loadAll;
        }
        // Start loop
        if (!loop) {
            loop = true;
            window.requestAnimationFrame(scrollFrame);
        }
    }

    // Load full resolution
    async load() {
        const webpsupport = await supportsWebp();

        const src = `${this.props.src}?${QueryString.stringify({
            w: this.props.loadWidth ? Math.min(this.props.loadWidth, MAX_WIDTH) : MAX_WIDTH,
            fm: webpsupport ? 'webp' : undefined,
            q: 50
        })}`;

        const x2 = `${this.props.src}?${QueryString.stringify({
            w: this.props.loadWidth ? Math.min(this.props.loadWidth * 2, MAX_WIDTH) : MAX_WIDTH,
            fm: webpsupport ? 'webp' : undefined,
            q: 50
        })}`;

        if (!this._isMounted) {
            return;
        }

        this.setState(
            {
                loading: true,
                src: src,
                x2: x2
            },
            () => {
                if (this.imageRef.current) {
                    let imageNode = this.imageRef.current;
                    const src = imageNode.currentSrc || this.state.src;
                    if (src) {
                        imageNode.src = src;
                        if (imageNode.complete) {
                            this.handleOnLoad.call(this);
                        } else {
                            imageNode.onload = this.handleOnLoad.bind(this);
                        }
                    }
                }
            }
        );
    }

    // Fade in full resolution
    componentDidUpdate(prevProps, prevState) {
        if (this.state.loaded && !prevState.loaded) {
            window.requestAnimationFrame(() => {
                if (this.imageRef.current) {
                    this.imageRef.current.style.opacity = 1;
                }
            });
        }
    }

    // Remove the loaded listener if the component will unmount
    componentWillUnmount() {
        this._isMounted = false;

        if (waiting[this.uid]) {
            delete waiting[this.uid];
        }

        if (this.imageRef.current && this.state.loaded === false) {
            let imageNode = this.imageRef.current;
            const src = imageNode.currentSrc || this.props.src;

            if (src) {
                imageNode.src = src;
                imageNode.onload = undefined;
            }
        }
    }

    // Handle load
    handleOnLoad() {
        this.setState({
            loaded: true
        });
    }

    render() {
        const aspect = typeof this.props.width === 'number' && typeof this.props.height === 'number';

        const containerStyle = aspect
            ? {
                height: 0,
                overflow: 'hidden',
                paddingBottom: this.props.width && this.props.height ? `${(this.props.height / this.props.width) * 100}%` : 'initial',
                position: 'relative',
                width: '100%'
            }
            : {};

        const loading = this.props.loading && (
            <img
                className='image__loading pixelated'
                loading='eager'
                role='presentation'
                src={this.props.loading}
                style={{
                    ...{
                        opacity: this.state.loaded ? 0 : 1,
                        height: '100%',
                        position: 'absolute',
                        transition: 'opacity 2s',
                        width: '100%'
                    },
                    ...this.props.loadingStyle
                }} />
        );

        const image = (this.state.loading || this.state.loaded) && (
            <img
                alt={this.props.alt}
                className={this.props.className || ''}
                height={this.props.height}
                ref={this.imageRef}
                role={this.props.role}
                sizes={this.props.sizes}
                src={this.state.src}
                srcSet={this.props.sizes ?
                    `${this.state.src ? `${this.state.src} ${this.props.loadWidth}w` : ''}${this.state.x2 && `, ${this.state.x2} ${Math.min(this.props.loadWidth * 2, 4000)}w`}` :
                    this.state.x2 && `${this.state.x2} 2x`
                }
                style={{
                    ...{
                        opacity: 0,
                        position: aspect ? 'absolute' : 'relative',
                        top: 0,
                        transition: 'opacity .3s',
                        width: '100%'
                    },
                    ...this.props.style
                }}
                width={this.props.width} />
        );

        return (
            <div
                className={`image ${this.props.containerClassName || ''}`}
                ref={this.containerRef}
                style={{ backgroundColor: this.state.loaded ? '' : this.props?.backgroundColor, ...containerStyle, ...this.props.containerStyle }}>
                <noscript>
                    {this.props.link ?
                        <a href={this.props.link}>
                            <img
                                alt={this.props.alt}
                                className={this.props.className || ''}
                                height={this.props.height}
                                ref={this.imageRef}
                                role={this.props.role}
                                sizes={this.props.sizes}
                                src={this.props.src}
                                srcSet={this.props.sizes ?
                                    `${this.state.src ? `${this.state.src} ${this.props.loadWidth}w` : ''}${this.state.x2 && `, ${this.state.x2} ${Math.min(this.props.loadWidth * 2, 4000)}w`}` :
                                    this.state.x2 && `${this.state.x2} 2x`
                                }
                                style={{
                                    ...{
                                        width: '100%'
                                    },
                                    ...this.props.style
                                }}
                                width={this.props.width} />
                        </a> : <img
                            alt={this.props.alt}
                            className={this.props.className || ''}
                            height={this.props.height}
                            ref={this.imageRef}
                            role={this.props.role}
                            sizes={this.props.sizes}
                            src={this.props.src}
                            srcSet={this.props.sizes ?
                                `${this.state.src ? `${this.state.src} ${this.props.loadWidth}w` : ''}${this.state.x2 && `, ${this.state.x2} ${Math.min(this.props.loadWidth * 2, 4000)}w`}` :
                                this.state.x2 && `${this.state.x2} 2x`
                            }
                            style={{
                                ...{
                                    width: '100%'
                                },
                                ...this.props.style
                            }}
                            width={this.props.width} />
                    }
                </noscript>
                {aspect &&
                    <div
                        style={{
                            bottom: 0,
                            left: 0,
                            position: 'absolute',
                            right: 0,
                            top: 0
                        }}>
                        {loading}
                        {this.props.link ? (
                            <a href={this.props.link}>
                                {image}
                            </a>
                        ) : (
                            <>
                                {image}
                            </>
                        )}
                    </div>
                }

                {!aspect &&
                    <>
                        {loading}
                        {this.props.link ? (
                            <a href={this.props.link}>
                                {image}
                            </a>
                        ) : (
                            <>
                                {image}
                            </>
                        )}
                    </>
                }
            </div>
        );
    }

    static propTypes = {
        alt: PropTypes.string,
        className: PropTypes.string,
        containerClassName: PropTypes.string,
        containerStyle: PropTypes.object,
        height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        link: PropTypes.string,
        loading: PropTypes.string,
        loadingStyle: PropTypes.object,
        loadWidth: PropTypes.number,
        role: PropTypes.string,
        sizes: PropTypes.string,
        src: PropTypes.string,
        style: PropTypes.object,
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        backgroundColor: PropTypes.string,
    };

}
